import React, { useEffect, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';

import { AuthProvider, AuthContext } from "./utils/AuthContext";

import Layout from "./pages/Layout";
import Dashboard from "./pages/Dashboard";
import Legislation from "./pages/Legislation";
import BillProfile from "./pages/BillProfile";
import BillVersionCompare from "./pages/BillVersionCompare";
import OrgProfile from "./pages/OrgProfile";
import DivisionProfile from "./pages/DivisionProfile";
import Directory from "./pages/Directory";
import CreateProfile from "./pages/CreateProfile";
import Profile from "./pages/Profile";
import PublicSquare from "./pages/PublicSquare";
import NotFound from "./pages/NotFound";
import PopoverInitializer from './components/PopoverInitializer';
import Landing from "./pages/Landing";
import PrivateRoute from "./components/PrivateRoute";
import ResetPasswordForm from "./pages/ResetPasswordForm";
import ConfirmRegistration from "./pages/ConfirmRegistration";
import Teams from "./pages/Teams";
import Subscription from "./pages/Subscription";
import Admin from "./pages/Admin";
import PlansPage from "./pages/PlansPage";
import FAQ from "./pages/FAQ";
import InfoPage from "./pages/InfoPage";
import Overview from "./pages/Overview";
import Awards from "./pages/Awards";
import NominationAccept from "./pages/NominationAccept";
import NominationDecline from "./pages/NominationDecline";
import ClientFeedbackView from "./pages/ClientFeedbackView";
import ReportView from "./pages/ReportView";
import Notifications from "./pages/Notifications";
import AmbassadorPage from "./pages/AmbassadorPage";
import AmbassadorJoin from "./pages/AmbassadorJoin";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  const { auth, individualSubscription, workplaceSubscription, accessLoading } = useContext(AuthContext);

  // Legislation and Teams always require the individual/workplace subscription
  const indivGate = (page) => accessLoading ? null : individualSubscription ? page : <Navigate to="/subscribe?plan=individual" />;
  const workGate  = (page) => accessLoading ? null : workplaceSubscription  ? page : <Navigate to="/subscribe?plan=workplace" />;

  const homeRedirect = () => {
    if (!auth.user?.profile_id) return '/profile/create';
    const pendingPlan = sessionStorage.getItem('pending_plan');
    if (pendingPlan) {
      sessionStorage.removeItem('pending_plan');
      return `/subscribe?plan=${pendingPlan}`;
    }
    return '/dashboard';
  };

  return (
    <Routes>
      <Route path={"/" || "/home"}
        element={auth.isAuthenticated ? (
          homeRedirect() ? <Navigate to={homeRedirect()} /> : null
        ) : <Landing />}
      />

      {/* Public pages */}
      <Route path="/public-square" element={<PublicSquare />} />
      <Route path="/feedback/:token" element={<ClientFeedbackView />} />
      <Route path="/report/:token" element={<ReportView />} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/info" element={<InfoPage />} />
      <Route path="/overview" element={<Overview />} />
      <Route path="/awards/accept/:token" element={<NominationAccept />} />
      <Route path="/awards/decline/:token" element={<NominationDecline />} />
      <Route path="/join/:token" element={<AmbassadorJoin />} />

      {/* Private pages */}
      <Route path="/subscribe" element={<PrivateRoute><Subscription /></PrivateRoute>} />
      <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
      <Route path="/ambassador" element={<PrivateRoute><AmbassadorPage /></PrivateRoute>} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/legislation"
        element={<PrivateRoute>{indivGate(<Legislation />)}</PrivateRoute>}
      />
      <Route path="/legislation/bill/:bill_id"
        element={<PrivateRoute>{indivGate(<BillProfile />)}</PrivateRoute>}
      />
      <Route path="/legislation/bill/:bill_id/compare"
        element={<PrivateRoute>{indivGate(<BillVersionCompare />)}</PrivateRoute>}
      />
      <Route path="/teams"
        element={<PrivateRoute>{workGate(<Teams />)}</PrivateRoute>}
      />
      <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
      <Route path="/awards" element={<PrivateRoute><Awards /></PrivateRoute>} />
      <Route path="/directory" element={<PrivateRoute><Directory /></PrivateRoute>} />
      <Route path="/organization/:id" element={<PrivateRoute><OrgProfile /></PrivateRoute>} />
      <Route path="/division/:id" element={<PrivateRoute><DivisionProfile /></PrivateRoute>} />
      <Route path="/profile/create" element={<PrivateRoute><CreateProfile /></PrivateRoute>} />
      <Route path="/profile/:id" element={<PrivateRoute><Profile /></PrivateRoute>} />
      <Route path="/reset-password/:token" element={<ResetPasswordForm />} />
      <Route path="/confirm-email/:token" element={<ConfirmRegistration />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Layout />
          <AppRoutes />
        </BrowserRouter>
        <PopoverInitializer />
      </AuthProvider>
    </HelmetProvider>
  );
}
